import React, { useState } from 'react';
import { sendContactEmail } from '../services/api';

function ContactTab() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await sendContactEmail(formData);

      if (data.success) {
        setFormStatus({
          submitted: true,
          error: false,
          message: data.message || 'Message sent. We will correspond shortly.'
        });

        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus({
          submitted: false,
          error: true,
          message: data.error || 'Transmission failed. Please attempt again.'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Connection error. Please try again later.'
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setFormStatus({ submitted: false, error: false, message: '' });
      }, 5000);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-8 md:p-16 font-serif text-stone-900">

      {/* Header Section */}
      <div className="mb-16 border-b border-stone-300 pb-8">
        <p className="text-xs font-sans tracking-[0.2em] text-stone-500 uppercase mb-2">
          Museome
        </p>
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight">Inquiries</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-24">

        {/* LEFT COLUMN: Contact Info */}
        <div className="md:col-span-4 space-y-12">
          <div>
            <h3 className="text-xl italic font-medium mb-4">Contact</h3>
            <p className="text-stone-600 font-light leading-relaxed">
              For questions or for giving more detailed information regarding the creation of your masterpiece, you can contact us directly via phone, email or this contact form. We are always happy to be in touch personally.
            </p>
          </div>

          <div className="space-y-8">
            {/* Phone */}
            <div className="flex items-start space-x-4 group">
              <div className="pt-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-stone-400 group-hover:text-stone-900 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h4 className="font-sans text-xs tracking-widest uppercase text-stone-500 mb-1">Telephone</h4>
                <p className="text-lg">(+44) 7922 760307</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start space-x-4 group">
              <div className="pt-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-stone-400 group-hover:text-stone-900 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-sans text-xs tracking-widest uppercase text-stone-500 mb-1">Email</h4>
                <p className="text-lg">marty@museome.com</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: The Form */}
        <div className="md:col-span-8">

          {/* Status Messages - Minimalist Style */}
          {formStatus.submitted && (
            <div className="mb-8 p-4 bg-stone-100 border-l-2 border-stone-900 text-stone-800 italic">
              {formStatus.message}
            </div>
          )}

          {formStatus.error && (
            <div className="mb-8 p-4 bg-red-50 border-l-2 border-red-900 text-red-900 italic">
              {formStatus.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Name Input */}
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="block w-full px-0 py-2 bg-transparent border-b border-stone-300 focus:border-stone-900 focus:outline-none transition-colors peer"
                />
                <label
                  htmlFor="name"
                  className="absolute left-0 -top-3.5 text-stone-400 text-xs font-sans tracking-widest uppercase transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-stone-800 peer-focus:text-xs"
                >
                  Name
                </label>
              </div>

              {/* Email Input */}
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="block w-full px-0 py-2 bg-transparent border-b border-stone-300 focus:border-stone-900 focus:outline-none transition-colors peer"
                />
                <label
                  htmlFor="email"
                  className="absolute left-0 -top-3.5 text-stone-400 text-xs font-sans tracking-widest uppercase transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-stone-800 peer-focus:text-xs"
                >
                  Email
                </label>
              </div>
            </div>

            {/* Subject Input */}
            <div className="relative">
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder=" "
                className="block w-full px-0 py-2 bg-transparent border-b border-stone-300 focus:border-stone-900 focus:outline-none transition-colors peer"
              />
              <label
                htmlFor="subject"
                className="absolute left-0 -top-3.5 text-stone-400 text-xs font-sans tracking-widest uppercase transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-stone-800 peer-focus:text-xs"
              >
                Subject
              </label>
            </div>

            {/* Message Input */}
            <div className="relative mt-8">
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="4"
                placeholder=" "
                className="block w-full px-0 py-2 bg-transparent border-b border-stone-300 focus:border-stone-900 focus:outline-none transition-colors resize-none peer"
              ></textarea>
              <label
                htmlFor="message"
                className="absolute left-0 -top-3.5 text-stone-400 text-xs font-sans tracking-widest uppercase transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-stone-800 peer-focus:text-xs"
              >
                Message
              </label>
            </div>

            {/* Submit Button - Minimalist Block */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className={`group relative px-10 py-4 bg-stone-900 text-white font-sans text-xs tracking-[0.2em] uppercase transition-all hover:bg-stone-700 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Transmitting...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ContactTab;
